import { useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, Grid, Link, Stack, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useStyle } from "./style";
import { CheckboxWrapper } from "@common/CheckboxWrapper/CheckboxWrapper";
import { uanNumber } from "@utils/regex";
import { WORDPRESS_URL } from "@constants";
import { useDeviceType } from "@hooks/useDeviceType";
import { useBroadbandUanForm } from "./useBroadbandUanForm";
import { IEirSalesAscertain } from "@modules/eirSalesFacade/interfaces/models/IEirSalesAscertain";
import { IEirSalesSaveAscertainPayload } from "@modules/eirSalesFacade/interfaces/payloads/IEirSalesSaveAscertainPayload";
import { KeyedMutator } from "swr";
import { ErrorUANHandler } from "./ErrorUANHandler";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { IError } from "itsf-ui-common";
import { ErrorHelperText } from "@common/ErrorHelperText/ErrorHelperText";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";

interface IBroadbandUanFormProps {
    ascertain?: IEirSalesAscertain;
    isLoading: boolean;
    error?: IError;
    mutateAscertain: KeyedMutator<IEirSalesAscertain>;
    saveAscertain(
        payload: IEirSalesSaveAscertainPayload,
        successCallback?: (ascertain: IEirSalesAscertain) => void
    ): Promise<void>;
    onAnswerChange?(): void;
    onDelete?(): void;
    onValidationChange?(hasErrors: boolean): void;
    editTrigger?: boolean;
    onEditHandled?(): void;
    shouldForceValidate?: boolean;
    onAccordionExpandedChange?(expanded: boolean): void;
}

export const BroadbandUanForm = (props: IBroadbandUanFormProps) => {
    const {
        ascertain,
        isLoading,
        error,
        saveAscertain,
        mutateAscertain,
        onAnswerChange,
        onDelete,
        onValidationChange,
        editTrigger,
        onEditHandled,
        shouldForceValidate,
        onAccordionExpandedChange,
    } = props;
    const { t } = useTranslation(["cart", "validation", "common"]);
    const { classes } = useStyle();
    const { isMobile } = useDeviceType();

    const {
        hasUan,
        isEditing,
        accordionExpanded,
        control,
        errors,
        handleSubmit,
        handleCancel,
        handleEdit,
        triggerValidation,
        hasQaFailedError,
        hasOtherError,
        shouldShowErrorContent,
        handleRetry,
        handleChangeUan,
    } = useBroadbandUanForm({
        ascertain,
        isLoading,
        error,
        saveAscertain,
        mutateAscertain,
        onAnswerChange,
        onValidationChange,
    });

    useEffect(() => {
        if (!editTrigger) {
            return;
        }

        handleEdit();
        onEditHandled?.();
    }, [editTrigger, handleEdit, onEditHandled]);

    const lastForceValidateRef = useRef<boolean>(false);
    useEffect(() => {
        if (!shouldForceValidate) {
            lastForceValidateRef.current = false;

            return;
        }
        if (lastForceValidateRef.current) {
            return;
        }
        lastForceValidateRef.current = true;
        triggerValidation();
    }, [shouldForceValidate, triggerValidation]);

    useEffect(() => {
        onAccordionExpandedChange?.(accordionExpanded);
    }, [accordionExpanded, onAccordionExpandedChange]);

    return (
        <Accordion className={classes.accordion} expanded={accordionExpanded} onChange={(_, expanded) => expanded}>
            {hasUan && !hasQaFailedError && !hasOtherError && (
                <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />} />
            )}
            <AccordionDetails className={classes.accordionDetails}>
                <HandleErrorAndLoading isLoading={isLoading && !shouldShowErrorContent}>
                    <ErrorUANHandler
                        hasOtherError={hasOtherError}
                        hasQaFailedError={hasQaFailedError}
                        shouldShowErrorContent={shouldShowErrorContent}
                        onChangeUan={handleChangeUan}
                        onRemove={onDelete}
                        onRetry={handleRetry}
                    />
                    {!shouldShowErrorContent && (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Typography className={classes.instructionBold} variant="normal">
                                        {t("cart:broadband.UAN.instruction")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <div className={classes.uanInputContainer}>
                                        <Controller
                                            control={control}
                                            name="uan"
                                            render={(renderProps) => (
                                                <LabeledTextField
                                                    error={Boolean(errors.uan)}
                                                    fullWidth
                                                    helperText={
                                                        errors.uan
                                                            ? t("cart:broadband.UAN.error.invalidUan")
                                                            : t("cart:broadband.UAN.helper")
                                                    }
                                                    inputRef={renderProps.ref}
                                                    isRequired
                                                    labelText={t("cart:broadband.UAN.label")}
                                                    name={renderProps.name}
                                                    placeholder={t("cart:broadband.UAN.placeholder")}
                                                    value={renderProps.value}
                                                    onChange={renderProps.onChange}
                                                />
                                            )}
                                            rules={{
                                                required: t("cart:broadband.UAN.error.invalidUan"),
                                                pattern: {
                                                    value: uanNumber,
                                                    message: t("cart:broadband.UAN.error.invalidUan"),
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography className={classes.helpText} variant="small">
                                        {t("cart:broadband.UAN.help")}
                                        <Link
                                            className={classes.helpLink}
                                            href={`${WORDPRESS_URL}/new-home-broadband`}
                                            rel="noopener"
                                            target="_blank"
                                        >
                                            {t("cart:broadband.UAN.helpLink")}
                                        </Link>
                                    </Typography>
                                </Grid>
                                <Grid className={classes.checkboxContainer} item xs={12}>
                                    <CheckboxWrapper
                                        control={control}
                                        error={errors.cancelHomePhone}
                                        isRequired
                                        label={t("cart:broadband.homePhoneCancel")}
                                        name="cancelHomePhone"
                                    />
                                    {errors.cancelHomePhone && (
                                        <ErrorHelperText message={t("cart:broadband.UAN.error.checkboxRequired")} />
                                    )}
                                </Grid>
                                <Grid item marginTop={1} xs={12}>
                                    <Stack
                                        flexDirection={isMobile ? "column-reverse" : "row"}
                                        gap={2}
                                        justifyContent={isMobile ? "center" : "flex-end"}
                                    >
                                        {isEditing && (
                                            <LabeledButton
                                                labelText={t("common:cancel")}
                                                variant="text"
                                                onClick={handleCancel}
                                            >
                                                {t("common:cancel")}
                                            </LabeledButton>
                                        )}
                                        <LabeledButton labelText={t("cart:broadband.UAN.confirm")} type="submit">
                                            {t("cart:broadband.UAN.confirm")}
                                        </LabeledButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </HandleErrorAndLoading>
            </AccordionDetails>
        </Accordion>
    );
};

BroadbandUanForm.displayName = "BroadbandUanForm";
